import { useMemo, useState, type FormEvent } from "react";
import { Money, asDecimalString } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createReturn, listReturns } from "../../api/returnsApi";
import { listSalesExt } from "../../api/salesApi";
import type { ReturnOut } from "../../api/types";
import { StatusBanner } from "../../components/StatusBanner";
import { BranchSelect } from "../../components/BranchSelect";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";
import { canWriteReturns } from "../../roles/permissions";

const RETURN_COLUMNS: DataTableColumn<ReturnOut, keyof ReturnOut>[] = [
  { key: "id", header: "Return", render: (r) => <span className="muted">{r.id.slice(0, 8)}</span> },
  { key: "sale_id", header: "Sale", render: (r) => <span className="muted">{r.sale_id.slice(0, 8)}</span> },
  { key: "refund_total", header: "Refund", align: "right", render: (r) => r.refund_total },
  { key: "reason", header: "Reason", render: (r) => r.reason ?? "—" },
  { key: "lines", header: "Lines", align: "right", render: (r) => r.lines.length },
];

/**
 * Returns/refunds — pick a Sale, choose which line items and quantities to return, submit
 * (§15.1). `POST /returns` reverses the ledger and restocks the returned quantity
 * server-side (`returns_service.create_return`); this page only ever sends deltas
 * (`ReturnLineCreate.quantity` for THIS return), never a cumulative total, since the
 * backend itself tracks already-returned quantity per sale line and rejects (409) an
 * over-return.
 */
export function ReturnsPage() {
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user ? canWriteReturns(user.role) : false;
  const returnsState = useAsyncData(() => ensureFreshToken().then((token) => listReturns(token)), []);

  const [branchId, setBranchId] = useState("");
  const salesState = useAsyncData(() => ensureFreshToken().then((token) => listSalesExt(token, branchId || undefined)), [branchId]);

  const [selectedSaleId, setSelectedSaleId] = useState("");
  const selectedSale = useMemo(() => (salesState.data ?? []).find((s) => s.id === selectedSaleId) ?? null, [salesState.data, selectedSaleId]);

  const [returnQty, setReturnQty] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  function selectSale(saleId: string) {
    setSelectedSaleId(saleId);
    setReturnQty({});
    setFormError(null);
    setSuccess(null);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!selectedSale) return;
    setFormError(null);
    setSuccess(null);
    const lines = Object.entries(returnQty)
      .filter(([, qty]) => qty && Money.isPositive(asDecimalString(qty)))
      .map(([saleItemId, qty]) => ({ sale_item_id: saleItemId, quantity: asDecimalString(qty) }));
    if (lines.length === 0) {
      setFormError("Enter a quantity to return for at least one line.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      const created = await createReturn(token, { sale_id: selectedSale.id, reason: reason || undefined, lines });
      setSuccess(`Return recorded — refund total ${created.refund_total}. Stock restocked and the ledger reversed for this sale.`);
      setReturnQty({});
      setReason("");
      returnsState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not record return.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Returns &amp; Refunds</h1>
          <p className="page__subtitle">
            Pick a sale, choose which lines/quantities to return, and submit — restocks inventory and reverses the
            ledger for the refunded amount.
          </p>
        </div>
      </div>

      {returnsState.error ? <StatusBanner kind="error">{returnsState.error}</StatusBanner> : null}
      {!canWrite && user ? (
        <StatusBanner kind="info">
          Your role ({user.role}) can view returns. Recording a new return is restricted to OWNER/BRANCH_MANAGER —
          the backend deliberately keeps this off TELLER to avoid arbitrary self-service refunds.
        </StatusBanner>
      ) : null}

      {canWrite ? (
        <div className="panel">
          <h2 className="panel__title">New return</h2>
          <div className="form-grid">
            <BranchSelect id="return-branch" label="Branch (filters sales list)" value={branchId} onChange={setBranchId} allOptionLabel="All branches" />
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="return-sale">
                Sale
              </label>
              <select
                id="return-sale"
                className="sl-input"
                value={selectedSaleId}
                onChange={(e) => selectSale(e.target.value)}
                disabled={salesState.loading}
              >
                <option value="">Select a sale…</option>
                {(salesState.data ?? []).map((sale) => (
                  <option key={sale.id} value={sale.id}>
                    {sale.id.slice(0, 8)} — {sale.grand_total} ({sale.items.length} item{sale.items.length === 1 ? "" : "s"})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedSale ? (
            <form onSubmit={handleSubmit}>
              <h3 className="panel__title" style={{ fontSize: "0.95rem" }}>
                Lines
              </h3>
              {selectedSale.items.map((item) => (
                <div key={item.id} className="leg-row">
                  <div className="sl-field">
                    <span className="sl-field__label">Item</span>
                    <span className="muted">{item.inventory_item_id.slice(0, 8)}</span>
                  </div>
                  <div className="sl-field">
                    <span className="sl-field__label">Sold qty</span>
                    <span>{item.quantity}</span>
                  </div>
                  <div className="sl-field">
                    <span className="sl-field__label">Unit price</span>
                    <span>{item.unit_price}</span>
                  </div>
                  <Input
                    label="Return qty"
                    inputMode="decimal"
                    value={returnQty[item.id] ?? ""}
                    onChange={(e) => setReturnQty((prev) => ({ ...prev, [item.id]: e.target.value }))}
                    placeholder="0"
                  />
                </div>
              ))}
              <div className="form-grid" style={{ marginTop: 8 }}>
                <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. customer changed their mind, damaged item" />
              </div>
              {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
              {success ? <StatusBanner kind="info">{success}</StatusBanner> : null}
              <div className="form-actions">
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Recording…" : "Record return"}
                </Button>
              </div>
            </form>
          ) : (
            <p className="muted">Select a sale above to choose which lines to return.</p>
          )}
        </div>
      ) : null}

      <div className="panel">
        {returnsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={RETURN_COLUMNS} rows={returnsState.data ?? []} getRowId={(row) => row.id} emptyMessage="No returns recorded yet." caption="Returns" />
        )}
      </div>
    </div>
  );
}
